import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, CircleAlert, Download, FileText, Loader, Squircle } from 'lucide-react';
import { Customer, Document, DocumentRequirement } from '../data/mockData';
import { useDataSource } from '../context/DataSourceContext';
import { useTitle } from '../context/TitleContext';
import { getCustomerByID } from '../services/dataService';
import { format } from 'date-fns';
import ReminderButton from './ReminderButton';
import ProtectedComponent from './ProtectedComponent';
import { HelpTooltip } from './HelpTooltip';

const CaseDocuments = () => {
  const { t } = useTranslation();
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { useAwsDynamoDB, awsConfig } = useDataSource();
  const { setTitle } = useTitle();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  useEffect(() => {
    const loadCustomer = async () => {
      if (!caseId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const data = await getCustomerByID(caseId, useAwsDynamoDB, awsConfig);
        setCustomer(data || null);
      } catch (err) {
        console.error('Error loading customer details:', err);
        setError(t('errorLoadingData'));
      } finally {
        setLoading(false);
      }
    };

    loadCustomer();
  }, [caseId, useAwsDynamoDB, awsConfig, t]);

  // Update both the header and document title when viewing a specific case
  useEffect(() => {
    if (caseId) {
      // Update both the header title and document title
      setTitle(caseId);
      document.title = caseId;
    }
    
    // Cleanup: Title will be reset by the AdminDashboard component
  }, [caseId, setTitle]);

  // Simulate file download function
  const handleDownload = (fileName: string) => {
    setDownloadingFile(fileName);
    
    // Simulate download delay
    setTimeout(() => {
      setDownloadingFile(null);
      // In a real app, you would initiate an actual download here
      alert(`File "${fileName}" would be downloaded in a real application.`);
    }, 1500);
  };

  // Find the uploaded document for a requirement
  const findUploadedDocument = (requirementId: number): Document | undefined => {
    if (!customer) return undefined;
    return customer.uploadedDocuments.find(doc => doc.requirementId === requirementId);
  };

  // Handle reminder sent
  const handleReminderSent = (updatedCustomer: Customer) => {
    setCustomer(updatedCustomer);
  };

  // Custom back function that preserves dashboard state
  const handleBackToDashboard = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="flex flex-col items-center">
          <Loader className="animate-spin h-12 w-12 text-blue-500 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('loadingData')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <CircleAlert className="mx-auto h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {error}
        </h2>
        <button
          onClick={handleBackToDashboard}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {t('backToDashboard')}
        </button>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <Squircle className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t('caseNotFound')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {t('caseIdNotExist')}
        </p>
        <button
          onClick={handleBackToDashboard}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {t('backToDashboard')}
        </button>
      </div>
    );
  }

  const { name, phoneNumber, requiredDocuments, optionalDocuments, uploadedDocuments } = customer;

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy HH:mm');
  };

  // Count completed required documents
  const completedRequiredCount = requiredDocuments.filter(req => 
    uploadedDocuments.some(doc => doc.requirementId === req.id)
  ).length;

  // Count completed optional documents
  const completedOptionalCount = optionalDocuments.filter(req => 
    uploadedDocuments.some(doc => doc.requirementId === req.id)
  ).length;

  // Render a document requirement item
  const renderDocumentRequirement = (requirement: DocumentRequirement, isRequired: boolean) => {
    const uploadedDoc = findUploadedDocument(requirement.id);
    const isCompleted = !!uploadedDoc;
    
    return (
      <li 
        key={requirement.id} 
        className={`px-6 py-4 flex items-start border-b border-gray-100 dark:border-gray-700 last:border-0 
          ${isCompleted 
            ? 'bg-green-50 dark:bg-green-900/20' 
            : isRequired 
              ? 'bg-yellow-50 dark:bg-yellow-900/10' 
              : ''
          }`}
      >
        <div className="flex-shrink-0 mt-1">
          {isCompleted ? (
            <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
          ) : (
            isRequired ? (
              <Squircle className="h-5 w-5 text-yellow-500" />
            ) : (
              <FileText className="h-5 w-5 text-gray-400" />
            )
          )}
        </div>
        <div className="ml-3 flex-1">
          <div className="flex justify-between">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              {t(requirement.nameKey)}
            </h4>
            {isRequired && !isCompleted && (
              <span className="text-xs font-medium text-yellow-600 dark:text-yellow-400 px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                {t('required')}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t(requirement.descriptionKey)}
          </p>
          
          {isCompleted && uploadedDoc && (
            <div className="mt-2 bg-white dark:bg-gray-800 rounded-md p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('uploadedOn')}: {formatDate(uploadedDoc.uploadDate)}
                  </p>
                  <p className="text-sm font-medium mt-1 break-all">
                    {uploadedDoc.fileName} ({uploadedDoc.fileSize})
                  </p>
                </div>

                <ProtectedComponent 
                  requiredPermission="download_documents"
                  disabledTooltip="You need download permissions to access this."
                >
                        <button
                    onClick={() => handleDownload(uploadedDoc.fileName)}
                    disabled={downloadingFile === uploadedDoc.fileName}
                    className="ml-2 p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full disabled:opacity-50"
                    title={t('download')}
                  >
                    {downloadingFile === uploadedDoc.fileName ? (
                      <Loader className="h-5 w-5 animate-spin" />
                    ) : (
                      <Download className="h-5 w-5" />
                    )}
                  </button>
                </ProtectedComponent>

              </div>
            </div>
          )}
        </div>
      </li>
    );
  };

  const hasMissingDocuments = completedRequiredCount < requiredDocuments.length;

  return (
    <div className="max-w-4xl mx-auto">
      {useAwsDynamoDB && (
        <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 p-3 mb-6 flex items-center">
          <div className="w-full text-center text-sm text-blue-700 dark:text-blue-300">
            {t('usingDynamoDB')} ({awsConfig.tableName})
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden mb-8">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('documentDetails', { caseId })}
          </h2>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('customerName')}
              </p>
              <p className="mt-1 text-base text-gray-900 dark:text-white">{name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('phoneNumber')}
              </p>
              <p className="mt-1 text-base text-gray-900 dark:text-white">{phoneNumber}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('requiredDocuments')}
              </p>
              <div className="mt-1 flex items-center space-x-2">
                <span
                  className={`font-medium ${
                    completedRequiredCount === 0
                      ? 'text-red-500'
                      : completedRequiredCount < requiredDocuments.length
                      ? 'text-yellow-500'
                      : 'text-green-500'
                  }`}
                >
                  {completedRequiredCount}/{requiredDocuments.length} {t('complete')}
                </span>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                {t('optionalDocuments')}
              </p>
              <div className="mt-1 flex items-center space-x-2">
                <span className="font-medium text-blue-500">
                  {completedOptionalCount}/{optionalDocuments.length} {t('provided')}
                </span>
              </div>
            </div>
          </div>
          
          {hasMissingDocuments && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('documentReminderH3Text')} <HelpTooltip tipKey='documentReminderHelpBtn' />
              </h3>
              <ReminderButton
                customer={customer}
                onReminderSent={handleReminderSent}
              />
            </div>
          )}
        </div>
      </div>

      {/* Required Documents Section */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden mb-8">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {t('requiredDocuments')}
          </h3>
        </div>
        
        {requiredDocuments.length > 0 ? (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {requiredDocuments.map(req => renderDocumentRequirement(req, true))}
          </ul>
        ) : (
          <div className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('noRequiredDocuments')}
          </div>
        )}
      </div>

      {/* Optional Documents Section */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {t('optionalDocuments')}
          </h3>
        </div>
        
        {optionalDocuments.length > 0 ? (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {optionalDocuments.map(req => renderDocumentRequirement(req, false))}
          </ul>
        ) : (
          <div className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('noOptionalDocuments')}
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseDocuments;
